import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sessionStorage } from '@shared/helpers/storage'
import User from '@shared/data/models/user.js'
import {
  getDefaultLanguage,
  getLangCodeByLanguage,
  getSupportedLangCodes,
  normalizeLang,
} from '@shared/utils'
import { changeLocale } from '@shared/localization.js'
import env from '@shared/env.js'
import * as apiRequest from '@shared/api-endpoints'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore'

export const useUserStore = defineStore(
  'user',
  () => {
    // State
    /** @type {import('vue').Ref<User|null>} */
    const user = ref(null)
    /** @type {import('vue').Ref<string>} */
    const lang = ref(getDefaultLanguage())
    /** @type {import('vue').Ref<string>} */
    const country = ref(env.VITE_DEFAULT_COUNTRY_CODE)
    const featureFlagStore = useFeatureFlagStore()

    // Actions
    function applyUserLanguage(userLanguage) {
      const supportedLangCodes = getSupportedLangCodes()
      let langCode = getLangCodeByLanguage(userLanguage)
      if (!supportedLangCodes.includes(langCode)) {
        langCode = supportedLangCodes[0]
        userLanguage = normalizeLang(langCode)
      }
      changeLocale(langCode, document.documentElement)
      lang.value = userLanguage
    }

    /** @return {Promise<User|null>} */
    async function storeSession(response) {
      const userData = response.data?.user
      const newUser = userData && new User(userData)
      user.value = newUser

      applyUserLanguage(newUser?.profile?.language)

      if (typeof newUser?.guid === 'string' && newUser.guid.length > 0) {
        await featureFlagStore.loadRemoteFeatureFlags()
      } else {
        featureFlagStore.resetRemoteFlags()
      }

      // Prefer profile corporate for checkout flows where users can switch
      // insurance before the current subscription corporate is updated.
      const corporateId =
        newUser?.profile?.corporateId ||
        newUser?.currentSubscription?.corporateId
      if (corporateId) {
        await apiRequest.loadCorporate({ id: corporateId })
      }

      return newUser
    }

    return {
      // State
      user,
      lang,
      country,
      // Actions
      storeSession,
    }
  },
  {
    persist: {
      storage: sessionStorage,
      serializer: {
        serialize: (state) => {
          const rawState = { ...state }
          if (rawState.user) {
            rawState.user = rawState.user._json
          }
          return JSON.stringify(rawState)
        },
        deserialize: (value) => {
          const parsed = JSON.parse(value)
          // Reconstruct User (to restore non-primitive properties)
          if (parsed.user) {
            parsed.user = new User(parsed.user)
          }
          return parsed
        },
      },
    },
  },
)

export default useUserStore
